// Port of TrailMap/MapLayer.swift (TrailBivvyKit): user-selectable map layers and the persisted layer preferences.
//
// `MapLayerPreferences` is a Swift struct with mutating methods; here it is an immutable value plus free functions
// (`toggleMapLayer` returns a new value). Persistence uses a synchronous `localStorage`-like store, like Swift's
// `UserDefaults`, under the same key "TrailBivvy.mapLayers.v1" and the same JSON shape.

import type { POICategory } from '../core/models';

/** How a layer is drawn. */
export type MapLayerKind =
  /** A base map; exactly one is shown. */
  | 'base'
  /** Raster tiles drawn over the base map (WMTS overlays). */
  | 'rasterOverlay'
  /** Geometry drawn by the app from local data (route, protected areas, suitability cells). */
  | 'vectorOverlay'
  /** Point-of-interest markers. */
  | 'poi';

/** User-selectable map layers (raw values identical to Swift). */
export type MapLayer =
  | 'topographic'
  | 'satellite'
  | 'trail'
  | 'hikingRoutes'
  | 'elevation'
  | 'slope'
  | 'protectedAreas'
  | 'wildlifeZones'
  | 'bivouacSuitability'
  | 'huts'
  | 'campsites'
  | 'water'
  | 'food'
  | 'transit';

/** Every layer, in display order (Swift `allCases`). */
export const allMapLayers: readonly MapLayer[] = [
  'topographic',
  'satellite',
  'trail',
  'hikingRoutes',
  'elevation',
  'slope',
  'protectedAreas',
  'wildlifeZones',
  'bivouacSuitability',
  'huts',
  'campsites',
  'water',
  'food',
  'transit',
];

export function isMapLayer(value: unknown): value is MapLayer {
  return typeof value === 'string' && (allMapLayers as readonly string[]).includes(value);
}

/**
 * How the layer is drawn. `protectedAreas` and `wildlifeZones` are vector overlays from the local (offline) dataset;
 * on the Swiss provider `wildlifeZones` additionally shows the official FOEN WMTS rasters.
 */
export function mapLayerKind(layer: MapLayer): MapLayerKind {
  switch (layer) {
    case 'topographic':
    case 'satellite':
      return 'base';
    case 'hikingRoutes':
    case 'elevation':
    case 'slope':
      return 'rasterOverlay';
    case 'trail':
    case 'protectedAreas':
    case 'wildlifeZones':
    case 'bivouacSuitability':
      return 'vectorOverlay';
    case 'huts':
    case 'campsites':
    case 'water':
    case 'food':
    case 'transit':
      return 'poi';
  }
}

/** `true` for `topographic` and `satellite`. */
export function isBaseLayer(layer: MapLayer): boolean {
  return mapLayerKind(layer) === 'base';
}

/** POI categories shown by a `poi` layer (empty for other kinds). Car parks are on no layer (iOS PK-3). */
export function mapLayerPOICategories(layer: MapLayer): POICategory[] {
  switch (layer) {
    case 'huts':
      return ['mountainHut', 'bivouacHut', 'shelter'];
    case 'campsites':
      return ['camping'];
    case 'water':
      return ['water'];
    case 'food':
      return ['foodShop', 'restaurant'];
    case 'transit':
      return ['train', 'bus', 'cableCar'];
    default:
      return [];
  }
}

/** Base layers in display order. */
export const baseMapLayers: readonly MapLayer[] = allMapLayers.filter(isBaseLayer);
/** Overlay layers (everything that is not a base layer) in display order. */
export const overlayMapLayers: readonly MapLayer[] = allMapLayers.filter((l) => !isBaseLayer(l));

// MARK: - Preferences

/**
 * The user's map layer choices. Invariants (enforced by `makeMapLayerPreferences`, decoding and toggling): `baseLayer`
 * is a base layer, `enabledOverlays` contains no base layer.
 */
export interface MapLayerPreferences {
  /** The single visible base map. */
  readonly baseLayer: MapLayer;
  /** Visible overlays (vector, raster and POI layers). */
  readonly enabledOverlays: ReadonlySet<MapLayer>;
  /** Map rotates with the compass heading. */
  readonly headingUp: boolean;
}

/** Default storage key (Swift `MapLayerPreferences.defaultsKey`). */
export const mapLayerPreferencesKey = 'TrailBivvy.mapLayers.v1';

export function makeMapLayerPreferences(
  baseLayer: MapLayer,
  enabledOverlays: Iterable<MapLayer>,
  headingUp = false,
): MapLayerPreferences {
  return {
    baseLayer: isBaseLayer(baseLayer) ? baseLayer : 'topographic',
    enabledOverlays: new Set([...enabledOverlays].filter((l) => !isBaseLayer(l))),
    headingUp,
  };
}

/** Topographic base map with trail, hiking routes, protected areas, wildlife zones, huts, campsites, water and transit. */
export const defaultMapLayerPreferences: MapLayerPreferences = makeMapLayerPreferences('topographic', [
  'trail',
  'hikingRoutes',
  'protectedAreas',
  'wildlifeZones',
  'huts',
  'campsites',
  'water',
  'transit',
]);

/** Whether `layer` is visible: the current base layer, or an enabled overlay. */
export function isMapLayerEnabled(preferences: MapLayerPreferences, layer: MapLayer): boolean {
  return isBaseLayer(layer) ? preferences.baseLayer === layer : preferences.enabledOverlays.has(layer);
}

/**
 * Overlays switch on/off. Base layers are exclusive: toggling a base layer selects it (toggling the current base layer
 * keeps it — a base map is always shown). Returns the new preferences.
 */
export function toggleMapLayer(preferences: MapLayerPreferences, layer: MapLayer): MapLayerPreferences {
  if (isBaseLayer(layer)) return { ...preferences, baseLayer: layer };
  const overlays = new Set(preferences.enabledOverlays);
  if (overlays.has(layer)) overlays.delete(layer);
  else overlays.add(layer);
  return { ...preferences, enabledOverlays: overlays };
}

export function sameMapLayerPreferences(a: MapLayerPreferences, b: MapLayerPreferences): boolean {
  if (a.baseLayer !== b.baseLayer || a.headingUp !== b.headingUp) return false;
  if (a.enabledOverlays.size !== b.enabledOverlays.size) return false;
  for (const layer of a.enabledOverlays) if (!b.enabledOverlays.has(layer)) return false;
  return true;
}

/** JSON shape (Swift `encode(to:)`): overlays as a sorted array of raw values. */
export function encodeMapLayerPreferences(preferences: MapLayerPreferences): string {
  return JSON.stringify({
    baseLayer: preferences.baseLayer,
    enabledOverlays: [...preferences.enabledOverlays].sort(),
    headingUp: preferences.headingUp,
  });
}

/**
 * Decodes the JSON shape. Unknown layer names (from a newer or older app version) are ignored instead of discarding
 * the whole preference; missing fields take the defaults. Throws on malformed JSON (callers fall back to defaults).
 */
export function decodeMapLayerPreferences(json: string): MapLayerPreferences {
  const value: unknown = JSON.parse(json);
  if (typeof value !== 'object' || value === null || Array.isArray(value)) throw new Error('not an object');
  const object = value as Record<string, unknown>;
  const base = isMapLayer(object.baseLayer) ? object.baseLayer : undefined;
  let overlays: MapLayer[] | undefined;
  if (Array.isArray(object.enabledOverlays)) overlays = object.enabledOverlays.filter(isMapLayer);
  else if (object.enabledOverlays !== undefined && object.enabledOverlays !== null) throw new Error('enabledOverlays');
  if (object.headingUp !== undefined && object.headingUp !== null && typeof object.headingUp !== 'boolean') {
    throw new Error('headingUp');
  }
  return makeMapLayerPreferences(
    base ?? defaultMapLayerPreferences.baseLayer,
    overlays ?? defaultMapLayerPreferences.enabledOverlays,
    object.headingUp === true,
  );
}

/** The part of `Storage` used for persistence (`localStorage` in the app, a stub in tests). */
export type PreferenceStorage = Pick<Storage, 'getItem' | 'setItem'>;

/** Loads preferences, falling back to the defaults when absent or unreadable. */
export function loadMapLayerPreferences(storage: PreferenceStorage, key = mapLayerPreferencesKey): MapLayerPreferences {
  let json: string | null;
  try {
    json = storage.getItem(key);
  } catch {
    return defaultMapLayerPreferences;
  }
  if (json === null) return defaultMapLayerPreferences;
  try {
    return decodeMapLayerPreferences(json);
  } catch (error) {
    console.error('Unreadable map layer preferences, using defaults', error);
    return defaultMapLayerPreferences;
  }
}

/** Saves the preferences as JSON (failures, e.g. a full or blocked storage, are logged). */
export function saveMapLayerPreferences(
  preferences: MapLayerPreferences,
  storage: PreferenceStorage,
  key = mapLayerPreferencesKey,
): void {
  try {
    storage.setItem(key, encodeMapLayerPreferences(preferences));
  } catch (error) {
    console.error('Cannot save map layer preferences', error);
  }
}
